import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, Copy, ArrowRight, Sparkles, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getBootLocale } from '../i18n';
import { initMetaPixel, pixelTrack, consumePendingPurchase, claimPurchaseOnce } from '../lib/metaPixel';

const PaymentSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const notifiedRef = useRef(false);
  const parentNotifiedRef = useRef(false);
  const isInIframe = typeof window !== 'undefined' && window.parent !== window;
  // Direction follows the boot locale so the donor's language reads naturally
  // — Hebrew payments stay RTL (legacy behavior), English donors get LTR.
  const locale = getBootLocale();
  const dir = locale === 'en' ? 'ltr' : 'rtl';

  // Notify parent window (iframe modal) immediately on mount
  useEffect(() => {
    if (!isInIframe || parentNotifiedRef.current) return;
    parentNotifiedRef.current = true;
    const payload: Record<string, string> = {};
    searchParams.forEach((v, k) => { payload[k] = v; });
    try {
      window.parent.postMessage({ type: 'amutech-payment-complete', payload }, '*');
    } catch {}
  }, [isInIframe, searchParams]);

  // Support both backend redirect params AND direct Hyp redirect params
  const amount = searchParams.get('amount') || searchParams.get('Amount');
  // transactionId — מסלול כרטיס-האייפרם של נדרים מעביר את מזהה העסקה בשם
  // הזה (NedarimCardModal.onSuccess), בעוד טרנזילה/Hyp שולחים tx/Id. בלי
  // הנפילה הזו העסקה מגיעה בלי מזהה: עמוד התודה לא מציג אותו, ואירוע
  // ה-Purchase של מטא מאבד גם את מפתח מניעת-הכפילות וגם את ה-eventID.
  const tranId = searchParams.get('tx') || searchParams.get('Id') || searchParams.get('transactionId');
  const planId = searchParams.get('plan') || '';
  const campaignSlug = searchParams.get('campaignSlug');
  const campaignName = searchParams.get('campaignName');
  const orgName = searchParams.get('orgName');
  const order = searchParams.get('Order') || '';
  const ccode = searchParams.get('CCode');
  // Date formatting locale-aware. Hebrew donors keep he-IL formatting,
  // English donors get en-US.
  const timestamp = new Date().toLocaleString(locale === 'en' ? 'en-US' : 'he-IL');

  // Detect payment type — from backend redirect or from Hyp Order field
  const orderHasCampaign = order.includes('campaign-');
  const isCampaignPayment = !!campaignSlug || planId.startsWith('campaign-') || orderHasCampaign;
  // Not every campaign is a donation drive — orgs also run event registrations,
  // tuition, ticketing and sales, and telling those payers "תודה על תרומתך" is
  // wrong. The campaign page sends `kind=payment` when the campaign is set to
  // issue a plain קבלה (receiptType='receipt'); donation campaigns send nothing
  // and keep the original wording, as does every other surface that lands here
  // (Hyp redirect, admin charge, links issued before this existed).
  const isPlainPayment = searchParams.get('kind') === 'payment';
  const isToolPayment = searchParams.get('toolPayment') === '1' || planId === 'tool-payment';
  const isCampaignDonation = isCampaignPayment && !isPlainPayment;
  const isSubscription = ['basic', 'pro', 'enterprise'].includes(planId);
  const isSms = planId.startsWith('sms-');
  const isSystemPayment = isSubscription || isSms;
  const paymentFailed = ccode !== null && ccode !== '0';

  // If we have Hyp params (Id, CCode, Sign) — send to backend to save the donation
  useEffect(() => {
    if (notifiedRef.current) return;
    const hypId = searchParams.get('Id');
    const hypCCode = searchParams.get('CCode');
    if (!hypId || hypCCode !== '0') return;
    notifiedRef.current = true;

    // Forward all Hyp params to backend for donation saving + verification
    const params = new URLSearchParams();
    searchParams.forEach((value, key) => params.append(key, value));

    // Attach any custom fields stored before the Hyp redirect
    try {
      const stored = sessionStorage.getItem('pendingCustomFields');
      if (stored) {
        params.set('customFields', stored);
        sessionStorage.removeItem('pendingCustomFields');
      }
    } catch {}

    const apiBase = (import.meta.env.VITE_API_URL || '/api').replace(/\/$/, '');
    fetch(`${apiBase}/payments/success?${params.toString()}`, {
      headers: { 'Accept': 'application/json' },
    })
      .then(r => {
        console.log('[PaymentSuccess] Backend notified, status:', r.status);
        return r.json().catch(() => ({}));
      })
      .then(body => { console.log('[PaymentSuccess] Backend response:', body); })
      .catch(e => { console.warn('[PaymentSuccess] Backend notify failed:', e.message); });
  }, [searchParams]);

  // ── Meta Pixel: Purchase ──────────────────────────────────────────────────
  // זו נקודת האמת היחידה למדידת המרה. מגיעים לכאן רק אחרי שהסליקה החזירה
  // הצלחה, ולכן לא נספרות כאן לחיצות "שלח" שנפלו — אחרת מטא הייתה לומדת
  // לחפש בדיוק את מי שנוטש.
  //
  // מזהה הפיקסל לא נמצא בשורת הכתובת (יש בה רק amount ו-tx); הוא מגיע
  // מההפקדה שדף הקמפיין השאיר רגע לפני פתיחת הסליקה. אין הפקדה = אין
  // פיקסל לקמפיין הזה = לא נטען שום סקריפט מעקב.
  useEffect(() => {
    // הדף הזה נטען גם בתוך iframe הסליקה, ומיד אחר כך שוב כחלון עליון אחרי
    // ההפניה של החלון-האב. יורים רק בטעינה העליונה: היא נשארת פתוחה מספיק
    // זמן כדי שהאירוע ייצא, וה-iframe נסגר תוך רגע.
    if (isInIframe) return;
    if (!isCampaignDonation || paymentFailed) return;

    const pending = consumePendingPurchase();
    if (!pending) return;
    // אותה מלכודת כמו ב-InitiateCheckout: מאתחלים ומעבירים הלאה את הרשימה
    // שהתקבלה, במקום להשאיר את השליחה תלויה במצב פנימי של המודול.
    const pixelIds = initMetaPixel(pending.pixelIds);
    if (!pixelIds.length) return;
    // הסכום שהסליקה אישרה גובר על מה שנבחר בטופס (תשלומים/עדכון סכום).
    const charged = Number(amount);
    const value = Number.isFinite(charged) && charged > 0 ? charged : pending.value;
    if (!value || value <= 0) return;
    if (!claimPurchaseOnce(tranId || '')) return;

    pixelTrack('PageView', undefined, undefined, pixelIds);
    pixelTrack(
      'Purchase',
      {
        value,
        currency: pending.currency || 'ILS',
        content_type: 'product',
        ...(pending.campaignName ? { content_name: pending.campaignName } : {}),
        ...(pending.campaignId ? { content_ids: [pending.campaignId] } : {}),
        ...(pending.ambassadorSlug ? { ambassador: pending.ambassadorSlug } : {}),
      },
      tranId || undefined,
      pixelIds,
    );
  }, [isInIframe, isCampaignDonation, paymentFailed, amount, tranId]);

  // Plan labels stay Hebrew-fixed for now — these are SaaS subscription tiers
  // shown to admins (Israeli orgs), not to donors. Translating is low-value.
  const planLabels: Record<string, string> = {
    basic: 'Basic — ₪199/חודש',
    pro: 'Pro — ₪349/חודש',
    enterprise: 'Enterprise — ₪599/חודש',
  };

  const handleCopy = () => {
    if (tranId) {
      navigator.clipboard.writeText(tranId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (isInIframe) {
    return (
      <div dir={dir} className="min-h-screen flex items-center justify-center bg-white" style={{ fontFamily: "'Heebo', sans-serif" }}>
        <div className="text-center">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-3" />
          <p className="text-gray-700 font-semibold">{t('public.success.received')}</p>
        </div>
      </div>
    );
  }

  return (
    <div dir={dir} className="min-h-screen bg-gradient-to-b from-green-50 to-blue-50 flex items-center justify-center p-4 md:p-6" style={{ fontFamily: "'Heebo', sans-serif" }}>
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="flex justify-center mb-8">
          <span style={{ fontSize: 28, fontWeight: 800, color: '#1d4ed8' }}>Amutech</span>
        </div>

        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-5 md:p-8 text-center">
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <CheckCircle size={80} className="text-green-500" />
              <div className="absolute inset-0 animate-ping opacity-20">
                <CheckCircle size={80} className="text-green-500" />
              </div>
            </div>
          </div>

          {/* ══════════ Campaign Donation ══════════ */}
          {isCampaignDonation && (
            <>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">{t('public.success.donationThanks')}</h1>
              {orgName && (
                <p className="text-gray-600 text-lg mb-1">
                  {t('public.success.donationOrgPrefix')}<strong className="text-gray-800">{orgName}</strong>
                </p>
              )}
              {campaignName && (
                <p className="text-gray-500 text-base mb-6">
                  {t('public.success.campaignLabel')}<strong className="text-gray-700">{campaignName}</strong>
                </p>
              )}
            </>
          )}

          {/* ══════════ Campaign Payment (not a donation) ══════════ */}
          {/* Same layout as the donation block above, neutral wording — an
              event registration / tuition / sale is a payment, not a gift. */}
          {isCampaignPayment && isPlainPayment && (
            <>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 mb-2">{t('public.success.paymentReceived')}</h1>
              {orgName && (
                <p className="text-gray-600 text-lg mb-1">
                  {t('public.success.paymentOrgPrefix')}<strong className="text-gray-800">{orgName}</strong>
                </p>
              )}
              {campaignName && (
                <p className="text-gray-500 text-base mb-6">
                  {t('public.success.campaignLabel')}<strong className="text-gray-700">{campaignName}</strong>
                </p>
              )}
            </>
          )}

          {/* ══════════ Subscription Plan ══════════ */}
          {isSubscription && (
            <>
              <h1 className="text-3xl font-black text-gray-900 mb-2">
                <Sparkles size={28} className="inline text-yellow-500 ml-2" />
                {t('public.success.subscriptionUpgraded')}
              </h1>
              <p className="text-gray-600 text-lg mb-2">
                {t('public.success.subscriptionWelcome')}<strong className="text-blue-600">{planLabels[planId] || planId}</strong>
              </p>
              <p className="text-gray-500 text-sm mb-6">
                {t('public.success.subscriptionFeatures')}
              </p>
            </>
          )}

          {/* ══════════ SMS Package ══════════ */}
          {isSms && (
            <>
              <h1 className="text-3xl font-black text-gray-900 mb-2">
                <MessageSquare size={28} className="inline text-green-500 ml-2" />
                {t('public.success.smsPurchased')}
              </h1>
              <p className="text-gray-600 text-lg mb-6">
                {t('public.success.smsCredits')}
              </p>
            </>
          )}

          {/* ══════════ Unknown / Fallback ══════════ */}
          {/* isCampaignPayment (not isCampaignDonation) — a plain-קבלה campaign
              already rendered its own block above and must not also fall here. */}
          {isToolPayment && (
            <>
              <h1 className="text-3xl font-black text-gray-900 mb-2">התשלום בוצע בהצלחה</h1>
              <p className="text-gray-600 text-lg mb-6">אישור הבקשה יגיע למייל לאחר בדיקת העמותה.</p>
            </>
          )}
          {!isCampaignPayment && !isSystemPayment && !isToolPayment && (
            <>
              <h1 className="text-3xl font-black text-gray-900 mb-2">{t('public.success.paymentSucceeded')}</h1>
              <p className="text-gray-600 text-lg mb-6">{t('public.success.received')}</p>
            </>
          )}

          {/* Details Box — text-align flips with direction so labels and
              values read in the donor's native order. */}
          <div className={`bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 space-y-3 mb-8 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
            {amount && (
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-semibold">{t('public.success.amount')}</span>
                <span className="text-2xl font-bold text-green-600">₪{Number(amount).toLocaleString(locale === 'en' ? 'en-US' : 'he-IL')}</span>
              </div>
            )}
            {tranId && (
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-semibold">{t('public.success.transactionId')}</span>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-gray-700">{tranId}</span>
                  <button onClick={handleCopy} className="p-1 hover:bg-white rounded transition-all" title={t('public.success.copy')}>
                    <Copy size={16} className={copied ? 'text-green-500' : 'text-gray-500'} />
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-semibold">{t('public.success.date')}</span>
              <span className="text-gray-700 text-sm">{timestamp}</span>
            </div>
          </div>

          <div className="border-t border-gray-200 my-6" />

          {/* Actions */}
          <div className="space-y-3">
            {isSystemPayment ? (
              <a
                href="/dashboard"
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 no-underline"
              >
                <ArrowRight size={20} />
                {t('public.success.backToDashboard')}
              </a>
            ) : (
              // Preserve the donor's locale (?lang=en) when returning to the
              // campaign page so the back nav doesn't snap them to Hebrew.
              <a
                href={campaignSlug ? `/campaign/${campaignSlug}${locale === 'en' ? '?lang=en' : ''}` : 'javascript:history.back()'}
                onClick={!campaignSlug ? (e: any) => { e.preventDefault(); window.history.go(-2); } : undefined}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 no-underline"
              >
                <ArrowRight size={20} />
                {t('public.success.backToCampaign')}
              </a>
            )}
          </div>

          {!isSystemPayment && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4 text-center">
              <p className="text-green-700 text-sm font-medium">{t('public.success.receiptSent')}</p>
            </div>
          )}
        </div>

        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            {t('public.success.supportPrompt')}<a href="mailto:support@amutech.co.il" className="text-blue-600 hover:underline font-semibold">{t('public.success.supportLink')}</a>
          </p>
        </div>
      </div>

      <style>{`@import url('https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700;800;900&display=swap');`}</style>
    </div>
  );
};

export default PaymentSuccessPage;
